import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";

const STORAGE_KEY = "themeEditorChanges";
const STYLE_ELEMENT_ID = "theme-editor-overrides";
const COLOR_PROPERTIES = /^(color|caret-color|fill|stroke|background(-color|-image)?|outline(-color)?|box-shadow|border(-(top|right|bottom|left))?(-color)?|-webkit-text-fill-color)$/;
const ANCESTORS_TO_SCAN = 12;
const SCOPES_TO_OFFER = 3;
const MAX_COMPONENT_ELEMENTS = 1500;
// Classes, attributes and pseudo-classes that switch an element into another state. Rules that need one of them
// are listed for the picked element as "when ..." even while the state is off.
const STATE_TOKENS = /\.[\w-]*(active|selected|checked|expanded|opened|open|current|disabled|focused|error|invalid|highlight|pressed|toggled)[\w-]*|\[(aria-(selected|expanded|checked|pressed|current|disabled)|disabled|checked|selected|open)[^\]]*\]|:(checked|disabled|indeterminate|invalid|placeholder-shown)\b/gi;
// Custom elements that are too generic to scope a change to
const NOT_A_SCOPE = /^(mat|cdk|ng)-|^app-(root|dashboard|dashboard-cont)$/;
const SAFE_SCOPE = /^[\w\-. ]+$/;
// A color from a parent only reaches the element for these; borders and shadows don't carry over
const INHERITED_FAMILIES = new Set(["color", "fill", "stroke", "background"]);
const EMPTY_VALUE = /^(transparent|none|inherit|initial|unset|currentcolor|0|0px|rgba\(0, 0, 0, 0\))$/i;
const HAS_COLOR = /#[0-9a-f]{3,8}\b|rgba?\(|hsla?\(|\b(white|black|red|blue|green|gray|grey)\b/i;
// Angular Material's own variables; not theme roles a designer should edit
const LIBRARY_VARIABLES = /^--(mdc|mat|ck)-/;
const LONGHAND_COLOR: Record<string, string> = {
  background: "background-color",
  border: "border-top-color",
  "border-top": "border-top-color",
  "border-right": "border-right-color",
  "border-bottom": "border-bottom-color",
  "border-left": "border-left-color",
  outline: "outline-color",
};

// States a rule can apply in; stripped from the selector so hover colors are listed too
const STATE_SELECTORS = /::?(hover|active|focus-visible|focus-within|focus|before|after|placeholder|-webkit-[a-z-]+)/g;
const THEME_FILES: Record<string, string> = {
  owtma: "src/assets/styles/owtma-theme.scss",
};

interface Change {
  value: string;
  was: string;
  token?: string;
  // CSS selector the change is limited to; none means the whole theme
  scope?: string;
}

interface ScopeOption {
  selector: string;
  label: string;
}

interface ThemeChanges {
  overrides: Record<string, Change>;
  flags: string[];
}

interface ColorRow {
  token: string;
  value: string;
  hex: string | null;
  alpha: number;
  uses: string;
  scope?: string;
  invalid?: boolean;
}

interface ParsedDeclaration {
  property: string;
  family: string;
  value: string;
  important: boolean;
  token: string | null;
}

interface ParsedRule {
  branches: string[];
  declarations: ParsedDeclaration[];
  order: number;
}

interface Candidate extends ParsedDeclaration {
  state: string;
  specificity: number[];
  order: number;
}

interface FixedColor {
  where: string;
  value: string;
  count?: number;
}

interface ComponentRoot {
  element: Element;
  label: string;
}

type EditorTab = "element" | "component" | "all" | "changes";

// Lets a designer preview changes to theme variables across the site and export only what she touched.
// Changes live in this browser (localStorage) and are applied through one injected <style>.
@Component({
  selector: "app-theme-editor",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./theme-editor.component.html",
  styleUrls: ["./theme-editor.component.scss"],
})
export class ThemeEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();
  @Output() changeCount = new EventEmitter<number>();

  @ViewChild("highlight") highlight?: ElementRef<HTMLElement>;

  tab: EditorTab = "element";
  picking = false;
  dockLeft = false;
  search = "";
  copied = false;
  theme = "default";

  pickedLabel = "";
  elementRows: ColorRow[] = [];
  fixedColors: FixedColor[] = [];
  scopeOptions: ScopeOption[] = [];
  componentRoots: ComponentRoot[] = [];
  componentRootIndex = 0;
  componentRows: ColorRow[] = [];
  componentFixed: FixedColor[] = [];
  componentElementCount = 0;
  scope = "";
  allRows: ColorRow[] = [];
  changeRows: ColorRow[] = [];

  private pickedElement: Element | null = null;
  private changes: Record<string, ThemeChanges> = {};
  private observer?: MutationObserver;
  private canvas = document.createElement("canvas").getContext("2d");

  constructor(private zone: NgZone) {}

  ngOnInit() {
    this.changes = this.load();
    this.theme = this.currentTheme();
    this.applyStyles();
    this.observer = new MutationObserver(() => this.onBodyClassChange());
    this.observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    this.refreshChangeRows();
    Promise.resolve().then(() => this.emitCount());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["open"] && !changes["open"].firstChange) {
      if (this.open) {
        this.refreshAll();
      } else {
        this.stopPicking();
      }
    }
  }

  ngOnDestroy() {
    this.stopPicking();
    this.observer?.disconnect();
    document.getElementById(STYLE_ELEMENT_ID)?.remove();
  }

  get current(): ThemeChanges {
    return this.changes[this.theme] ?? { overrides: {}, flags: [] };
  }

  get flags(): string[] {
    return this.current.flags;
  }

  get changedCount(): number {
    return Object.keys(this.current.overrides).length + this.current.flags.length;
  }

  get filteredAllRows(): ColorRow[] {
    const search = this.search.trim().toLowerCase();
    return search ? this.allRows.filter((row) => row.token.includes(search)) : this.allRows;
  }

  private keyFor(token: string, scope?: string): string {
    return scope ? `${scope}||${token}` : token;
  }

  private tokenOf(key: string): string {
    return this.current.overrides[key]?.token ?? key;
  }

  isChanged(row: ColorRow): boolean {
    return !!this.current.overrides[this.keyFor(row.token, row.scope)];
  }

  wasValue(row: ColorRow): string {
    return this.current.overrides[this.keyFor(row.token, row.scope)]?.was ?? "";
  }

  setScope(scope: string) {
    this.scope = scope;
    this.refreshElementRows();
    this.refreshComponentRows();
  }

  setComponentRoot(index: string) {
    this.componentRootIndex = Number(index);
    this.refreshComponentRows();
  }

  private rootsFor(element: Element): ComponentRoot[] {
    const roots: ComponentRoot[] = [{ element, label: `This element (${element.tagName.toLowerCase()})` }];
    for (let level = element.parentElement; level && level !== document.body; level = level.parentElement) {
      const tag = level.tagName.toLowerCase();
      if (tag.includes("-") && !NOT_A_SCOPE.test(tag)) {
        roots.push({ element: level, label: `<${tag}>` });
      }
    }
    if (element.tagName.includes("-")) {
      roots[0].label = `<${element.tagName.toLowerCase()}>`;
    }
    return roots;
  }

  // Every color that actually wins on a component and everything inside it, most used first.
  // Querying once per selector is far cheaper than testing each selector on each element.
  private refreshComponentRows() {
    const root = this.componentRoots[this.componentRootIndex]?.element;
    if (!root?.isConnected) {
      this.componentRows = [];
      this.componentFixed = [];
      this.componentElementCount = 0;
      return;
    }
    const isVisible = (element: Element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && getComputedStyle(element).visibility !== "hidden";
    };
    const elements = [root, ...Array.from(root.querySelectorAll("*")).filter(isVisible)].slice(0, MAX_COMPONENT_ELEMENTS);
    const inScope = new Set(elements);
    this.componentElementCount = elements.length;
    const candidates = new Map<Element, Candidate[]>(elements.map((element) => [element, this.ownCandidates(element)]));
    for (const rule of this.parsedRules()) {
      for (const branch of rule.branches) {
        const cleaned = branch.replace(/:not\(:(hover|active|focus[\w-]*)\)/g, "");
        const stripped = cleaned.replace(STATE_SELECTORS, "");
        if (!stripped) {
          continue;
        }
        let matches: Element[];
        try {
          matches = [...(root.matches(stripped) ? [root] : []), ...Array.from(root.querySelectorAll(stripped))];
        } catch {
          continue;
        }
        const hits = matches.filter((element) => inScope.has(element));
        if (hits.length) {
          const state = this.stateOf(cleaned);
          const specificity = this.specificity(branch);
          for (const element of hits) {
            for (const declaration of rule.declarations) {
              candidates.get(element)?.push({ ...declaration, state, specificity, order: rule.order });
            }
          }
        }
      }
    }
    const tokens = new Map<string, { element: Element; property: string; labels: Set<string>; elements: Set<Element> }>();
    const fixed = new Map<string, FixedColor>();
    // The root itself goes through inspect(), which adds what it inherits and its inactive states
    const own = this.inspect(root, true);
    for (const [token, labels] of own.tokens) {
      const entry = { element: root, property: Array.from(labels)[0].split(" ")[0], labels: new Set(labels), elements: new Set<Element>([root]) };
      tokens.set(token, entry);
    }
    for (const color of own.fixed) {
      fixed.set(`${color.where}|${color.value}`, { ...color, count: 1 });
    }
    for (const element of elements.slice(1)) {
      for (const winner of this.winners(candidates.get(element) ?? [])) {
        if (EMPTY_VALUE.test(winner.value)) {
          continue;
        }
        const label = winner.state ? `${winner.property} (${winner.state})` : winner.property;
        if (winner.token && !LIBRARY_VARIABLES.test(winner.token)) {
          const entry = tokens.get(winner.token) ?? { element, property: winner.property, labels: new Set<string>(), elements: new Set<Element>() };
          entry.labels.add(label);
          entry.elements.add(element);
          tokens.set(winner.token, entry);
        } else if (winner.token || HAS_COLOR.test(winner.value)) {
          const key = `${label}|${winner.value}`;
          const existing = fixed.get(key);
          fixed.set(key, { where: label, value: winner.value, count: (existing?.count ?? 0) + 1 });
        }
      }
    }
    // The picked element's own colors first, then the most used
    this.componentRows = Array.from(tokens.entries())
      .sort(([, a], [, b]) => Number(b.elements.has(root)) - Number(a.elements.has(root)) || b.elements.size - a.elements.size)
      .map(([token, entry]) => {
        const count = entry.elements.size;
        const uses = `${Array.from(entry.labels).join(", ")} · ${count} element${count === 1 ? "" : "s"}`;
        return { ...this.buildRow(token, uses, entry.element, entry.property), scope: this.scope || undefined };
      });
    this.componentFixed = Array.from(fixed.values()).sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  }

  // The element itself, then the components around it, narrowest first
  private scopesFor(element: Element): ScopeOption[] {
    const components: string[] = [];
    for (let level: Element | null = element; level && components.length < SCOPES_TO_OFFER; level = level.parentElement) {
      const tag = level.tagName.toLowerCase();
      if (tag.includes("-") && !NOT_A_SCOPE.test(tag)) {
        components.push(tag);
      }
    }
    const options = components.map((tag) => ({ selector: tag, label: `Only in <${tag}>` }));
    if (!components.length) {
      return options;
    }
    for (let level: Element | null = element; level && level.tagName.toLowerCase() !== components[0]; level = level.parentElement) {
      const name = Array.from(level.classList).find((value) => !/^(ng-|cdk-|mat-|mdc-)/.test(value) && /^[\w-]+$/.test(value));
      if (name) {
        options.unshift({ selector: `${components[0]} .${name}`, label: `Only this element (.${name})` });
        break;
      }
    }
    return options;
  }

  setTab(tab: EditorTab) {
    this.tab = tab;
    this.refreshAll();
  }

  // ---- Picking ----
  togglePicking() {
    this.picking ? this.stopPicking() : this.startPicking();
  }

  private startPicking() {
    this.picking = true;
    this.zone.runOutsideAngular(() => {
      window.addEventListener("mousemove", this.onPickMove, true);
      window.addEventListener("pointerdown", this.swallow, true);
      window.addEventListener("mousedown", this.swallow, true);
      window.addEventListener("mouseup", this.swallow, true);
      window.addEventListener("click", this.onPickClick, true);
    });
  }

  private stopPicking() {
    this.picking = false;
    window.removeEventListener("mousemove", this.onPickMove, true);
    window.removeEventListener("pointerdown", this.swallow, true);
    window.removeEventListener("mousedown", this.swallow, true);
    window.removeEventListener("mouseup", this.swallow, true);
    window.removeEventListener("click", this.onPickClick, true);
    this.moveHighlight(null);
  }

  private isOwnElement(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest("app-theme-editor, .theme-editor-btn");
  }

  private swallow = (event: Event) => {
    if (!this.isOwnElement(event.target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  private onPickMove = (event: MouseEvent) => {
    this.moveHighlight(this.isOwnElement(event.target) ? null : (event.target as Element));
  };

  private onPickClick = (event: MouseEvent) => {
    if (this.isOwnElement(event.target)) {
      return;
    }
    this.swallow(event);
    const element = event.target as Element;
    this.zone.run(() => {
      this.stopPicking();
      this.pick(element);
    });
  };

  private moveHighlight(element: Element | null) {
    const highlight = this.highlight?.nativeElement;
    if (!highlight) {
      return;
    }
    if (!element) {
      highlight.style.display = "none";
      return;
    }
    const rect = element.getBoundingClientRect();
    highlight.style.display = "block";
    highlight.style.top = `${rect.top}px`;
    highlight.style.left = `${rect.left}px`;
    highlight.style.width = `${rect.width}px`;
    highlight.style.height = `${rect.height}px`;
  }

  private pick(element: Element) {
    this.pickedElement = element;
    this.pickedLabel = this.describe(element);
    this.scopeOptions = this.scopesFor(element);
    this.scope = "";
    this.componentRoots = this.rootsFor(element);
    this.componentRootIndex = 0;
    this.tab = "component";
    this.refreshElementRows();
    this.refreshComponentRows();
  }

  // Works while a dropdown or menu is open: clicking the Pick button would close it
  @HostListener("window:keydown", ["$event"])
  onKeydown(event: KeyboardEvent) {
    if (!this.open) {
      return;
    }
    if (event.key === "Escape" && this.picking) {
      event.stopImmediatePropagation();
      this.stopPicking();
    } else if (event.altKey && event.shiftKey && event.code === "KeyC") {
      event.preventDefault();
      this.togglePicking();
    }
  }

  // ---- Which variables actually color an element ----
  // Every rule with a color, parsed once per pick. Order is the stylesheet order, the cascade's tie-breaker.
  private parsedRules(): ParsedRule[] {
    const rules: ParsedRule[] = [];
    const walk = (list: CSSRuleList) => {
      for (const rule of Array.from(list)) {
        if (rule instanceof CSSStyleRule) {
          const declarations = this.parseDeclarations(rule.style.cssText);
          if (declarations.length) {
            rules.push({ branches: this.splitTopLevel(rule.selectorText), declarations, order: rules.length });
          }
        } else if (rule instanceof CSSMediaRule) {
          if (window.matchMedia(rule.media.mediaText).matches) {
            walk(rule.cssRules);
          }
        } else if (rule instanceof CSSSupportsRule) {
          walk(rule.cssRules);
        }
      }
    };
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        walk(sheet.cssRules);
      } catch {
        // Cross-origin stylesheets (e.g. fonts) can't be read and hold no theme variables
      }
    }
    return rules;
  }

  private parseDeclarations(cssText: string): ParsedDeclaration[] {
    const declarations: ParsedDeclaration[] = [];
    for (const text of cssText.split(";")) {
      const separator = text.indexOf(":");
      if (separator < 0) {
        continue;
      }
      const property = text.slice(0, separator).trim();
      if (!COLOR_PROPERTIES.test(property)) {
        continue;
      }
      const important = /!important\s*$/.test(text);
      const value = text.slice(separator + 1).replace(/!important\s*$/, "").trim();
      // Only the outermost variable: var(--sh-x, var(--legacy, #fff)) is controlled by --sh-x
      const token = value.match(/var\(\s*(--[\w-]+)/)?.[1] ?? null;
      declarations.push({ property, family: this.familyOf(property), value, important, token });
    }
    return declarations;
  }

  // Declarations that compete with each other in the cascade
  private familyOf(property: string): string {
    if (property.startsWith("background")) {
      return "background";
    }
    if (property === "border" || property === "border-color") {
      return "border";
    }
    return property.replace(/-color$/, "");
  }

  private splitTopLevel(selector: string): string[] {
    const parts: string[] = [];
    let depth = 0;
    let current = "";
    for (const char of selector) {
      depth += char === "(" ? 1 : char === ")" ? -1 : 0;
      if (char === "," && depth === 0) {
        parts.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    parts.push(current.trim());
    return parts;
  }

  // [ids, classes, elements]. :not, :is and :has count as their most specific argument, :where as nothing.
  private specificity(selector: string): number[] {
    const total = [0, 0, 0];
    let rest = selector;
    for (let match = rest.match(/:(not|is|has|where)\(/); match; match = rest.match(/:(not|is|has|where)\(/)) {
      const open = (match.index as number) + match[0].length;
      let depth = 1;
      let close = open;
      while (close < rest.length && depth > 0) {
        depth += rest[close] === "(" ? 1 : rest[close] === ")" ? -1 : 0;
        close++;
      }
      if (match[1] !== "where") {
        const best = this.splitTopLevel(rest.slice(open, close - 1))
          .map((part) => this.specificity(part))
          .reduce((a, b) => (this.compareSpecificity(a, b) >= 0 ? a : b), [0, 0, 0]);
        best.forEach((value, index) => (total[index] += value));
      }
      rest = rest.slice(0, match.index) + rest.slice(close);
    }
    const count = (pattern: RegExp) => {
      const found = rest.match(pattern)?.length ?? 0;
      rest = rest.replace(pattern, " ");
      return found;
    };
    rest = rest.replace(/\([^)]*\)/g, "");
    total[0] += count(/#[\w-]+/g);
    total[2] += count(/::[\w-]+/g);
    total[1] += count(/\.[\w-]+/g) + count(/\[[^\]]*\]/g) + count(/:[\w-]+/g);
    total[2] += rest.match(/[a-zA-Z][\w-]*/g)?.length ?? 0;
    return total;
  }

  private compareSpecificity(a: number[], b: number[]): number {
    return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
  }

  // Hover and focus aren't active while picking, so states are stripped to match and kept as a label
  private matchBranch(element: Element, branch: string, includeInactiveStates = false): { state: string } | null {
    const cleaned = branch.replace(/:not\(:(hover|active|focus[\w-]*)\)/g, "");
    const matches = (selector: string) => {
      try {
        return !!selector && element.matches(selector);
      } catch {
        return false;
      }
    };
    if (matches(cleaned.replace(STATE_SELECTORS, ""))) {
      return { state: this.stateOf(cleaned) };
    }
    if (!includeInactiveStates) {
      return null;
    }
    // :not(...) keeps its contents: :not(.disabled) already matches when the state is off
    const kept: string[] = [];
    const masked = cleaned.replace(/:not\((?:[^()]|\([^()]*\))*\)/g, (group) => `\u0000${kept.push(group) - 1}\u0000`);
    const removed = Array.from(new Set(masked.match(STATE_TOKENS) ?? []));
    if (!removed.length) {
      return null;
    }
    const withoutStates = masked
      .replace(STATE_TOKENS, "")
      .replace(/\u0000(\d+)\u0000/g, (match, index) => kept[Number(index)])
      // A compound left empty, e.g. ".active > span" -> "> span", stands for any element
      .replace(/(^|[\s>+~])(?=[\s>+~]|$)/g, "$1*");
    if (!matches(withoutStates.replace(STATE_SELECTORS, ""))) {
      return null;
    }
    const pseudo = this.stateOf(withoutStates);
    return { state: [pseudo, `when ${removed.join("")}`].filter(Boolean).join(" ") };
  }

  private stateOf(cleaned: string): string {
    const found = cleaned.match(STATE_SELECTORS) ?? [];
    const names = Array.from(new Set(found.map((state) => state.replace(/^:+/, "").replace(/-(visible|within)$/, ""))));
    return names.sort().join(" ");
  }

  private candidatesFor(element: Element, rules: ParsedRule[], includeInactiveStates = false): Candidate[] {
    const candidates: Candidate[] = [];
    for (const rule of rules) {
      for (const branch of rule.branches) {
        const matched = this.matchBranch(element, branch, includeInactiveStates);
        if (matched) {
          const specificity = this.specificity(branch);
          for (const declaration of rule.declarations) {
            candidates.push({ ...declaration, state: matched.state, specificity, order: rule.order });
          }
        }
      }
    }
    return [...candidates, ...this.ownCandidates(element)];
  }

  // Presentation attributes lose to every rule; inline styles beat every rule
  private ownCandidates(element: Element): Candidate[] {
    const candidates: Candidate[] = [];
    for (const attribute of ["fill", "stroke"]) {
      const value = element.getAttribute(attribute);
      if (value && value !== "none") {
        for (const declaration of this.parseDeclarations(`${attribute}: ${value}`)) {
          candidates.push({ ...declaration, state: "", specificity: [0, 0, 0], order: -1 });
        }
      }
    }
    for (const declaration of this.parseDeclarations(element.getAttribute("style") ?? "")) {
      candidates.push({ ...declaration, state: "", specificity: [1000, 0, 0], order: Number.MAX_SAFE_INTEGER });
    }
    return candidates;
  }

  private beats(a: Candidate, b: Candidate): boolean {
    if (a.important !== b.important) {
      return a.important;
    }
    return this.compareSpecificity(a.specificity, b.specificity) > 0 || (this.compareSpecificity(a.specificity, b.specificity) === 0 && a.order > b.order);
  }

  // The declaration that wins for each family and state. In a state, plain rules still compete:
  // a plain !important rule beats a :hover rule. Pseudo-elements only compete among themselves.
  private winners(candidates: Candidate[]): Candidate[] {
    // Browser autofill isn't a state a designer styles
    const states = new Set(candidates.map((candidate) => candidate.state).filter((state) => !state.includes("webkit")));
    const result: Candidate[] = [];
    for (const state of states) {
      const pseudoElement = /before|after|placeholder|webkit/.test(state);
      const best = new Map<string, Candidate>();
      for (const candidate of candidates) {
        if (candidate.state === state || (!pseudoElement && candidate.state === "")) {
          const current = best.get(candidate.family);
          if (!current || this.beats(candidate, current)) {
            best.set(candidate.family, candidate);
          }
        }
      }
      for (const candidate of best.values()) {
        // In a state, a plain rule winning means the state has no color of its own
        if (candidate.state === state) {
          result.push(candidate);
        }
      }
    }
    return result;
  }

  private inspect(element: Element, includeInactiveStates = false): { tokens: Map<string, Set<string>>; fixed: FixedColor[] } {
    const tokens = new Map<string, Set<string>>();
    const fixed = new Map<string, FixedColor>();
    const rules = this.parsedRules();
    const resolved = new Set<string>();
    // Text often sits in an inner element with its own color (e.g. Material list items), so start there
    const levels: Element[] = [];
    const textHost = this.textHost(element);
    if (textHost) {
      levels.push(textHost);
    }
    const ownLevels = levels.length + 1;
    for (let level: Element | null = element; level && level !== document.body && levels.length <= ANCESTORS_TO_SCAN; level = level.parentElement) {
      levels.push(level);
    }
    levels.forEach((level, index) => {
      const isParent = index >= ownLevels;
      for (const winner of this.winners(this.candidatesFor(level, rules, includeInactiveStates && !isParent))) {
        const key = `${winner.family}|${winner.state}`;
        // Only inherited colors and backgrounds that show through come from a parent
        if (resolved.has(key) || (isParent && !INHERITED_FAMILIES.has(winner.family)) || EMPTY_VALUE.test(winner.value)) {
          continue;
        }
        resolved.add(key);
        const notes = [winner.state, isParent ? "parent" : ""].filter(Boolean).join(", ");
        const label = notes ? `${winner.property} (${notes})` : winner.property;
        if (winner.token && !LIBRARY_VARIABLES.test(winner.token)) {
          const uses = tokens.get(winner.token) ?? new Set<string>();
          uses.add(label);
          tokens.set(winner.token, uses);
        } else if (winner.token || HAS_COLOR.test(winner.value)) {
          fixed.set(label, { where: label, value: winner.value });
        }
      }
    });
    return { tokens, fixed: Array.from(fixed.values()) };
  }

  private textHost(element: Element): Element | null {
    const hasOwnText = (node: Element) => Array.from(node.childNodes).some((child) => child.nodeType === Node.TEXT_NODE && !!child.textContent?.trim());
    if (hasOwnText(element)) {
      return null;
    }
    return Array.from(element.querySelectorAll("*")).find(hasOwnText) ?? null;
  }

  private describe(element: Element): string {
    const owner = this.ownerComponent(element);
    const classes = Array.from(element.classList).filter((name) => !name.startsWith("ng-")).slice(0, 2);
    const name = element.tagName.toLowerCase() + classes.map((value) => `.${value}`).join("");
    const text = element.textContent?.trim().replace(/\s+/g, " ").slice(0, 30);
    return `${owner ? `<${owner}> ` : ""}${name}${text ? ` "${text}"` : ""}`;
  }

  private ownerComponent(element: Element): string {
    for (let level: Element | null = element; level; level = level.parentElement) {
      if (level.tagName.includes("-") && level.tagName !== "APP-ROOT") {
        return level.tagName.toLowerCase();
      }
    }
    return "";
  }

  // ---- Rows ----
  private contextElement(): Element {
    return this.pickedElement?.isConnected
      ? this.pickedElement
      : document.querySelector("app-dashboard .main-container") ?? document.body;
  }

  private buildRow(token: string, uses: string, context: Element, fallbackProperty?: string): ColorRow {
    const style = getComputedStyle(context);
    // Overlays render outside the page's dark scope, so the page may hold the value an overlay row needs
    const pageValue = () => getComputedStyle(document.querySelector("app-dashboard .main-container") ?? document.body).getPropertyValue(token).trim();
    let value = style.getPropertyValue(token).trim() || pageValue();
    if (!value && fallbackProperty) {
      value = style.getPropertyValue(LONGHAND_COLOR[fallbackProperty] ?? fallbackProperty).trim();
    }
    const parsed = this.parseColor(value);
    return { token, value, hex: parsed?.hex ?? null, alpha: parsed?.alpha ?? 1, uses };
  }

  private refreshElementRows() {
    if (!this.pickedElement?.isConnected) {
      this.elementRows = [];
      this.fixedColors = [];
      return;
    }
    const element = this.pickedElement;
    const { tokens, fixed } = this.inspect(element, true);
    this.fixedColors = fixed;
    this.elementRows = Array.from(tokens.entries()).map(([token, uses]) => {
      const list = Array.from(uses);
      return { ...this.buildRow(token, list.join(", "), element, list[0].split(" ")[0]), scope: this.scope || undefined };
    });
  }

  private refreshAllRows() {
    const tokens = new Set<string>();
    const collect = (list: CSSRuleList) => {
      for (const rule of Array.from(list)) {
        if (rule instanceof CSSStyleRule) {
          for (const property of Array.from(rule.style)) {
            if (property.startsWith("--sh-")) {
              tokens.add(property);
            }
          }
        } else if (rule instanceof CSSMediaRule || rule instanceof CSSSupportsRule) {
          collect(rule.cssRules);
        }
      }
    };
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        collect(sheet.cssRules);
      } catch {
        // See colorRules()
      }
    }
    const context = this.contextElement();
    this.allRows = Array.from(tokens)
      .sort()
      .map((token) => this.buildRow(token, "", context))
      .filter((row) => row.hex !== null);
  }

  private refreshChangeRows() {
    this.changeRows = Object.keys(this.current.overrides)
      .sort()
      .map((key) => {
        const { value, scope } = this.current.overrides[key];
        const parsed = this.parseColor(value);
        return { token: this.tokenOf(key), value, hex: parsed?.hex ?? null, alpha: parsed?.alpha ?? 1, uses: scope ? `Only in ${scope}` : "Everywhere", scope };
      });
  }

  private refreshAll() {
    this.refreshElementRows();
    if (this.tab === "component") {
      this.refreshComponentRows();
    }
    this.refreshAllRows();
    this.refreshChangeRows();
  }

  // ---- Editing ----
  onColorInput(row: ColorRow, hex: string) {
    this.setValue(row, this.formatColor(hex, row.alpha));
  }

  onAlphaInput(row: ColorRow, percent: string) {
    if (row.hex) {
      this.setValue(row, this.formatColor(row.hex, Number(percent) / 100));
    }
  }

  onTextInput(row: ColorRow, value: string) {
    this.setValue(row, value.trim());
  }

  // The theme's own value, with every change in this tool switched off
  private themeValue(row: ColorRow): string {
    const sheet = (document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null)?.sheet;
    if (sheet) {
      sheet.disabled = true;
    }
    const value = this.buildRow(row.token, "", this.contextElement()).value;
    if (sheet) {
      sheet.disabled = false;
    }
    return value || row.value;
  }

  private isValidValue(value: string): boolean {
    return !!value && !/[{};]/.test(value) && (CSS.supports("color", value) || CSS.supports("background", value));
  }

  private setValue(row: ColorRow, value: string) {
    row.invalid = !this.isValidValue(value);
    if (row.invalid) {
      return;
    }
    const current = this.ensureTheme();
    const key = this.keyFor(row.token, row.scope);
    const was = current.overrides[key]?.was ?? this.themeValue(row);
    if (value === was) {
      delete current.overrides[key];
    } else {
      current.overrides[key] = { value, was, token: row.token, scope: row.scope };
    }
    const parsed = this.parseColor(value);
    row.value = value;
    row.hex = parsed?.hex ?? null;
    row.alpha = parsed?.alpha ?? 1;
    this.commit();
    this.refreshChangeRows();
    this.syncRows(row);
  }

  // The same variable can be listed in several tabs
  private syncRows(source: ColorRow) {
    for (const row of [...this.elementRows, ...this.componentRows, ...this.allRows]) {
      if (row !== source && row.token === source.token && row.scope === source.scope) {
        row.value = source.value;
        row.hex = source.hex;
        row.alpha = source.alpha;
      }
    }
  }

  resetRow(row: ColorRow) {
    delete this.current.overrides[this.keyFor(row.token, row.scope)];
    this.commit();
    this.refreshAll();
  }

  resetAll() {
    delete this.changes[this.theme];
    this.commit();
    this.refreshAll();
  }

  flagForDeveloper() {
    if (this.pickedLabel) {
      const flag = `${location.pathname}: ${this.pickedLabel}`;
      const current = this.ensureTheme();
      if (!current.flags.includes(flag)) {
        current.flags.push(flag);
        this.commit();
      }
    }
  }

  removeFlag(flag: string) {
    this.current.flags = this.current.flags.filter((value) => value !== flag);
    this.commit();
  }

  private ensureTheme(): ThemeChanges {
    return (this.changes[this.theme] ??= { overrides: {}, flags: [] });
  }

  private commit() {
    this.applyStyles();
    this.save();
    this.emitCount();
  }

  private emitCount() {
    this.changeCount.emit(this.changedCount);
  }

  // ---- Export ----
  get exportText(): string {
    const { overrides, flags } = this.current;
    const line = (key: string, indent: string) => `${indent}${this.tokenOf(key)}: ${overrides[key].value}; // was ${overrides[key].was}`;
    const keys = Object.keys(overrides).sort();
    const global = keys.filter((key) => !overrides[key].scope);
    const roles = global.filter((key) => this.tokenOf(key).startsWith("--sh-"));
    const legacy = global.filter((key) => !this.tokenOf(key).startsWith("--sh-"));
    const scopes = Array.from(new Set(keys.map((key) => overrides[key].scope).filter((scope): scope is string => !!scope)));
    const lines = [`// Theme: ${this.theme}. Exported from the theme editor.`];
    if (THEME_FILES[this.theme]) {
      lines.push(`// File: ${THEME_FILES[this.theme]}`);
    }
    if (roles.length) {
      lines.push("", "// Everywhere: search for each variable in the file above and replace its value", ...roles.map((key) => line(key, "  ")));
    }
    if (legacy.length) {
      lines.push("", "// Everywhere, older variables: replace in @mixin owt-dark-vars, or in theme.service.ts for --customizable-*", ...legacy.map((key) => line(key, "  ")));
    }
    if (scopes.length) {
      lines.push("", `// Only in specific components: add inside ${this.themeSelector(this.theme)} { } in the file above`);
      for (const scope of scopes) {
        lines.push(`${scope} {`, ...keys.filter((key) => overrides[key].scope === scope).map((key) => line(key, "  ")), "}");
      }
    }
    if (flags.length) {
      lines.push("", "// Colors that aren't variables yet: a developer needs to make these editable", ...flags.map((flag) => `//   ${flag}`));
    }
    return lines.join("\n") + "\n";
  }

  async copyExport() {
    try {
      await navigator.clipboard.writeText(this.exportText);
    } catch {
      const area = document.createElement("textarea");
      area.value = this.exportText;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    this.copied = true;
    setTimeout(() => (this.copied = false), 2000);
  }

  downloadExport() {
    const url = URL.createObjectURL(new Blob([this.exportText], { type: "text/plain" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${this.theme}-theme-changes.scss`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ---- Styles and storage ----
  // Dark values are assigned on several elements (body, .main-container, overlay panes), so an override
  // has to be set on every element to win over all of them
  private applyStyles() {
    let style = document.getElementById(STYLE_ELEMENT_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ELEMENT_ID;
      document.head.appendChild(style);
    }
    const rules: string[] = [];
    for (const [theme, { overrides }] of Object.entries(this.changes)) {
      const body = this.themeSelector(theme);
      const byScope = new Map<string, string[]>();
      for (const [key, change] of Object.entries(overrides)) {
        const scope = change.scope ?? "";
        if (this.isValidValue(change.value) && (!scope || SAFE_SCOPE.test(scope))) {
          byScope.set(scope, [...(byScope.get(scope) ?? []), `${change.token ?? key}: ${change.value} !important;`]);
        }
      }
      for (const [scope, declarations] of byScope) {
        const target = scope ? `${body} ${scope}` : body;
        rules.push(`${target}, ${target} * { ${declarations.join(" ")} }`);
      }
    }
    style.textContent = rules.join("\n");
  }

  private themeSelector(theme: string): string {
    return theme === "default" ? 'body:not([class*="-theme"])' : `body.${theme}-theme`;
  }

  private currentTheme(): string {
    return document.body.className.match(/(?:^|\s)([\w-]+)-theme(?:\s|$)/)?.[1] ?? "default";
  }

  private onBodyClassChange() {
    const theme = this.currentTheme();
    if (theme !== this.theme) {
      this.zone.run(() => {
        this.theme = theme;
        this.emitCount();
        if (this.open) {
          this.refreshAll();
        } else {
          this.refreshChangeRows();
        }
      });
    }
  }

  private load(): Record<string, ThemeChanges> {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
    } catch {
      return {};
    }
  }

  private save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.changes));
    } catch {
      // Without storage the changes still apply until the page reloads
    }
  }

  // ---- Colors ----
  // Lets the browser parse any CSS color; a canvas rejects invalid values by keeping the previous one
  private parseColor(value: string): { hex: string; alpha: number } | null {
    if (!value || !this.canvas) {
      return null;
    }
    this.canvas.fillStyle = "#000000";
    this.canvas.fillStyle = value;
    const first = String(this.canvas.fillStyle);
    this.canvas.fillStyle = "#ffffff";
    this.canvas.fillStyle = value;
    if (first !== String(this.canvas.fillStyle)) {
      return null;
    }
    if (first.startsWith("#")) {
      return { hex: first, alpha: 1 };
    }
    const parts = first.match(/[\d.]+/g)?.map(Number);
    if (!parts || parts.length < 3) {
      return null;
    }
    const hex = parts.slice(0, 3).map((part) => part.toString(16).padStart(2, "0")).join("");
    return { hex: `#${hex}`, alpha: parts[3] ?? 1 };
  }

  private formatColor(hex: string, alpha: number): string {
    if (alpha >= 1) {
      return hex;
    }
    const [red, green, blue] = [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16));
    return `rgba(${red}, ${green}, ${blue}, ${Math.round(alpha * 100) / 100})`;
  }

  percent(alpha: number): number {
    return Math.round(alpha * 100);
  }
}
